import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Box,
    Chip,
    Divider,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Typography
} from '@mui/material';

//react-redux
import { useSelector } from 'react-redux';

// third-party
import { FormattedMessage } from 'react-intl';

// project imports
import useAuth from 'hooks/useAuth';
import { getRoleLabel } from 'utils/rbacUtils';

// assets
import { IconLogout, IconSettings } from '@tabler/icons-react';
import useConfig from 'hooks/useConfig';

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = () => {
    //redux state
    const account = useSelector((state) => state.account);

    const theme = useTheme();
    const { borderRadius } = useConfig();
    const navigate = useNavigate();

    const [sdm, setSdm] = useState(true);
    const [value, setValue] = useState('');
    const [notification, setNotification] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [open, setOpen] = useState(false);
    const { logout } = useAuth();
    /**
     * anchorRef is used on different components and specifying one type leads to other components throwing an error
     * */
    const anchorRef = useRef(null);
    const handleLogout = async () => {
        try {
            await logout();
        } catch (err) {
            console.error(err);
        }
    };

    const handleClose = () => setOpen(false);
    const handleListItemClick = (event, index, route = '') => {
        setSelectedIndex(index);
        handleClose(event);

        if (route && route !== '') {
            navigate(route);
        }
    };
    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    return (
        <>
            <Chip
                sx={{
                    height: '48px',
                    alignItems: 'center',
                    borderRadius: '27px',
                    transition: 'all .2s ease-in-out',
                    borderColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                    '&[aria-controls="menu-list-grow"], &:hover': {
                        borderColor: theme.palette.primary.main,
                        background: `${theme.palette.primary.main}!important`,
                        color: theme.palette.primary.light,
                        '& svg': {
                            stroke: theme.palette.primary.light
                        }
                    },
                    '& .MuiChip-label': {
                        lineHeight: 0
                    }
                }}
                icon={
                    <Avatar sx={{ width: '32px', height: '32px', marginRight: 1 }}>
                        {(account?.user?.full_name || account?.user?.email || account?.user?.displayName || 'U').charAt(0).toUpperCase()}
                    </Avatar>
                }
                label={<IconSettings stroke={1.5} size="24px" color={theme.palette.primary.main} />}
                variant="outlined"
                ref={anchorRef}
                aria-controls={open ? 'menu-list-grow' : undefined}
                aria-haspopup="true"
                onClick={handleToggle}
                color="primary"
            />

            <Menu
                anchorEl={anchorRef.current}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                keepMounted
            >
                <MenuItem disabled>
                    <Box display="flex" flexDirection="column">
                        <Typography variant="subtitle1">
                            {account?.user?.full_name || account?.user?.displayName || account?.user?.email || 'User'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {account?.user?.email || ''}
                        </Typography>
                        {getRoleLabel(account?.user) ? (
                            <Chip
                                size="small"
                                label={getRoleLabel(account?.user)}
                                sx={{ mt: 0.5, alignSelf: 'flex-start', fontSize: '0.7rem' }}
                                color={account?.user?.is_superuser ? 'error' : account?.user?.datahub_role === 'tenant_admin' ? 'primary' : 'default'}
                                variant="outlined"
                            />
                        ) : null}
                    </Box>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                        <IconLogout stroke={1.5} size="20px" />
                    </ListItemIcon>
                    <ListItemText primary={<Typography variant="body2"><FormattedMessage id="logout" /></Typography>} />
                </MenuItem>
            </Menu>
        </>
    );
};

export default ProfileSection;
